import { useState } from 'react';
import {
  Send,
  ArrowLeft,
  Loader2,
  CheckCircle2,
  UserPlus,
  Phone,
  Clock,
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { accessControlAction } from '@/lib/api';
import { isContactPickerSupported, pickContact } from '@/lib/contactPicker';
import { isValidPhoneNumber, normalizePhoneToE164 } from '@/lib/phoneUtils';

type FlowStep = 'form' | 'sending' | 'success' | 'error';

interface SendKeySmsFlowProps {
  deviceId: string;
  organizationId: string;
  deviceName: string;
  onBack: () => void;
}

const DURATION_OPTIONS = [
  { value: 1800, label: '30 minuter' },
  { value: 3600, label: '1 timme' },
  { value: 7200, label: '2 timmar' },
  { value: 86400, label: '24 timmar' },
] as const;

export function SendKeySmsFlow({
  deviceId,
  deviceName,
  onBack,
}: SendKeySmsFlowProps) {
  const [step, setStep] = useState<FlowStep>('form');
  const [visitorName, setVisitorName] = useState('');
  const [visitorPhone, setVisitorPhone] = useState('');
  const [durationSeconds, setDurationSeconds] = useState(3600);
  const [error, setError] = useState<string | null>(null);
  const [expiresAt, setExpiresAt] = useState('');
  const contactPickerAvailable = isContactPickerSupported();

  const handlePickContact = async () => {
    const contact = await pickContact();
    if (contact) {
      if (contact.phone) setVisitorPhone(contact.phone);
      if (contact.name && !visitorName) setVisitorName(contact.name);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    const phone = visitorPhone.trim();
    const name = visitorName.trim();

    if (!name || !phone) return;

    if (!isValidPhoneNumber(phone)) {
      setError('Ogiltigt telefonnummer. Ange i format 07XXXXXXXX eller +46XXXXXXXXX.');
      return;
    }

    setError(null);
    setStep('sending');

    // Hard timeout so the UI cannot sit in "skickar nyckel" forever if the
    // backend or upstream SMS provider stalls. 25s is comfortably longer than
    // the backend's own 46elks budget (~13s) but short enough to surface
    // clearly if something goes wrong.
    type SendKeyResult = {
      guest_key_id: string;
      expires_at: string;
      sms_sent: boolean;
      sms_error?: string;
    };

    const timeoutPromise = new Promise<{ data: SendKeyResult | null; error: string | null }>(
      (resolve) =>
        setTimeout(
          () =>
            resolve({
              data: null,
              error:
                'Det tog för lång tid att skicka nyckeln. Kontrollera telefonnumret och försök igen.',
            }),
          25000,
        ),
    );

    const result = await Promise.race([
      accessControlAction<SendKeyResult>('send-key-sms', {
        device_id: deviceId,
        visitor_name: name,
        visitor_phone: normalizePhoneToE164(phone),
        duration_seconds: durationSeconds,
      }),
      timeoutPromise,
    ]);

    if (result.error || !result.data) {
      setError(result.error || 'Kunde inte skicka nyckel');
      setStep('error');
      return;
    }

    if (!result.data.sms_sent) {
      const detail = result.data.sms_error ? ` (${result.data.sms_error})` : '';
      setError(`Nyckeln skapades men SMS kunde inte skickas${detail}`);
      setStep('error');
      return;
    }

    setExpiresAt(result.data.expires_at);
    setStep('success');
  };

  const handleReset = () => {
    setVisitorName('');
    setVisitorPhone('');
    setDurationSeconds(3600);
    setError(null);
    setExpiresAt('');
    setStep('form');
  };

  return (
    <div className="flex-1 px-4 py-6 space-y-6">
      <div>
        <h2 className="text-lg font-semibold">Skicka nyckel</h2>
        <p className="text-sm text-muted-foreground mt-1">
          {deviceName}
        </p>
      </div>

      {/* Form step */}
      {step === 'form' && (
        <Card className="animate-fade-in">
          <CardContent className="pt-6">
            <form onSubmit={handleSubmit} className="space-y-4">
              {/* Phone number */}
              <div className="space-y-2">
                <label htmlFor="visitor-phone" className="text-sm font-medium flex items-center gap-1.5">
                  <Phone className="h-3.5 w-3.5" />
                  Telefonnummer
                </label>
                <div className="flex gap-2">
                  <input
                    id="visitor-phone"
                    name="tel"
                    type="tel"
                    value={visitorPhone}
                    onChange={(e) => setVisitorPhone(e.target.value)}
                    placeholder="07XXXXXXXX"
                    required
                    autoFocus
                    autoComplete="tel"
                    inputMode="tel"
                    className="flex h-10 w-full rounded-md border border-input bg-background/50 px-3 py-2 text-sm ring-offset-background placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
                  />
                  {contactPickerAvailable && (
                    <Button
                      type="button"
                      variant="outline"
                      size="icon"
                      onClick={handlePickContact}
                      title="Välj från kontakter"
                      className="shrink-0"
                    >
                      <UserPlus className="h-4 w-4" />
                    </Button>
                  )}
                </div>
              </div>

              {/* Visitor name */}
              <div className="space-y-2">
                <label htmlFor="visitor-name" className="text-sm font-medium flex items-center gap-1.5">
                  <UserPlus className="h-3.5 w-3.5" />
                  Besökarens namn
                </label>
                <input
                  id="visitor-name"
                  name="name"
                  type="text"
                  value={visitorName}
                  onChange={(e) => setVisitorName(e.target.value)}
                  placeholder="Förnamn Efternamn"
                  required
                  maxLength={100}
                  autoComplete="name"
                  className="flex h-10 w-full rounded-md border border-input bg-background/50 px-3 py-2 text-sm ring-offset-background placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
                />
              </div>

              {/* Duration selector */}
              <div className="space-y-2">
                <label className="text-sm font-medium flex items-center gap-1.5">
                  <Clock className="h-3.5 w-3.5" />
                  Giltighetstid
                </label>
                <div className="grid grid-cols-2 gap-2">
                  {DURATION_OPTIONS.map((opt) => (
                    <button
                      key={opt.value}
                      type="button"
                      onClick={() => setDurationSeconds(opt.value)}
                      className={`rounded-lg border px-3 py-2 text-sm transition-colors ${
                        durationSeconds === opt.value
                          ? 'border-primary bg-primary/10 text-primary font-medium'
                          : 'border-border hover:border-primary/30 hover:bg-accent/50'
                      }`}
                    >
                      {opt.label}
                    </button>
                  ))}
                </div>
              </div>

              {/* Error */}
              {error && (
                <div className="rounded-lg border border-destructive/50 bg-destructive/10 p-3 text-sm text-destructive">
                  {error}
                </div>
              )}

              {/* Submit */}
              <Button
                type="submit"
                className="w-full"
                size="lg"
                disabled={!visitorName.trim() || !visitorPhone.trim()}
              >
                <Send className="h-5 w-5" />
                Skicka nyckel via SMS
              </Button>

              <Button
                type="button"
                variant="ghost"
                size="sm"
                className="w-full"
                onClick={onBack}
              >
                <ArrowLeft className="h-4 w-4" />
                Tillbaka
              </Button>
            </form>
          </CardContent>
        </Card>
      )}

      {/* Sending step */}
      {step === 'sending' && (
        <Card className="animate-fade-in">
          <CardContent className="pt-6 text-center space-y-4 py-12">
            <Loader2 className="h-10 w-10 animate-spin text-primary mx-auto" />
            <p className="text-lg font-medium">Skickar nyckel...</p>
            <p className="text-sm text-muted-foreground">
              Genererar nyckel och skickar SMS
            </p>
          </CardContent>
        </Card>
      )}

      {/* Success step */}
      {step === 'success' && (
        <Card className="animate-fade-in">
          <CardContent className="pt-6 text-center space-y-4">
            <div className="flex justify-center">
              <div className="flex h-16 w-16 items-center justify-center rounded-full bg-green-100 dark:bg-green-900/30">
                <CheckCircle2 className="h-8 w-8 text-green-600 dark:text-green-400" />
              </div>
            </div>

            <div>
              <p className="text-lg font-bold text-green-600 dark:text-green-400">
                Nyckel skickad!
              </p>
              <p className="text-sm text-muted-foreground mt-1">
                SMS skickat till <span className="font-medium">{visitorName}</span>
              </p>
              <p className="text-sm text-muted-foreground">
                {visitorPhone}
              </p>
            </div>

            {expiresAt && (
              <p className="text-xs text-muted-foreground">
                Giltig till{' '}
                {new Date(expiresAt).toLocaleString('sv-SE', {
                  hour: '2-digit',
                  minute: '2-digit',
                  day: 'numeric',
                  month: 'short',
                })}
              </p>
            )}

            <Button variant="outline" onClick={handleReset} className="mt-4">
              <Send className="h-4 w-4" />
              Skicka till en till
            </Button>
          </CardContent>
        </Card>
      )}

      {/* Error step */}
      {step === 'error' && (
        <Card className="animate-fade-in">
          <CardContent className="pt-6 text-center space-y-4">
            {error && (
              <div className="rounded-lg border border-destructive/50 bg-destructive/10 p-3 text-sm text-destructive">
                {error}
              </div>
            )}

            <div className="flex gap-2 justify-center">
              <Button variant="outline" onClick={() => { setError(null); setStep('form'); }}>
                Försök igen
              </Button>
              <Button variant="ghost" onClick={onBack}>
                Tillbaka
              </Button>
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  );
}
